import { ErrorHandler, inject, NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { HTTP_INTERCEPTORS, HttpBackend, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { MatDialogModule } from "@angular/material/dialog";
import { AWSAuthService } from "@intocare-medapp/aws/http";
import { IcOrganisation } from "@intocare-medapp/organisation/ic-organisation";
import { errorHandlerFactory } from "@intocare-medapp/errors";
import { ScrollModule } from "@intocare-medapp/ui";
import { IcEnvironment } from "@intocare-medapp/environment";

import { CordovaModule, LoadingIndicatorModule, S3ImageModule, SimpleToastModule } from "../shared/modules";

import { AppComponent } from "./app.component";
import { routing } from "./app.routing";
import { AuthSharedModule } from "../authentication/auth.module";
import { HomeSharedModule } from "../home/home.module";
import { MedicalIconModule } from "../medical-data/shared";

import { SharedModule } from "../shared/shared.module";
import { COMPONENT_MAP, DynamicFormsModule } from "../shared/modules/ngx-dynamic-forms/dynamic-forms.module";

import { CUSTOM_COMPONENT_DIRECTIVES, customComponentFactory } from "../shared/components/custom-dynamic-form";
import {
	createCacheBustMultiTranslateHttpLoader,
	LanguageService,
	LocalStorageService,
	PUB_SUB_MODELS,
	TIMER_MODELS,
} from "../shared";
import { DarListener } from "../dar-data/dar-listener";
import { MedicationIncidentListener } from "../medication/administration-medication-incident/services/medication-incident.listener";
import { FoundMedicationIncidentListener } from "../medication/found-medication-incident/services/found-medication-incident.listener";
import { SyncActionsModule } from "../sync-actions/sync-actions.module";
import { TokenInterceptor } from "@intocare-medapp/aws/http/interceptors/token-interceptor";
import { COGNITO_SERVICE_CONFIGURATION } from "@intocare-medapp/aws/http/tokens";
import { Store } from "@ngrx/store";
import { Router } from "@angular/router";
import { provideTranslateService, TranslateLoader } from "@ngx-translate/core";
import { provideTanStackQuery, QueryClient } from "@tanstack/angular-query-experimental";
import {
	AppEventLocalForageStorage,
	DefaultEventService,
	EVENT_SERVICE,
	PERSISTENT_APP_EVENT_STORE_TOKEN,
} from "@intocare-medapp/events";
import { AppAlertBannerComponent } from "./app-alert-banner.component";
import { WoundCareModel } from "../wound-care/wound-care.model";
import { WoundCareOverviewModule } from "../wound-care/overview/overview.module";
import { TasksModel } from "../tasks/tasks.model";
import { TasksSharedModule } from "../tasks/tasks.module";
import { MedicalDataModel } from "../medical-data/medical-data.model";
import { MedDataOverviewModule } from "../medical-data/overview";
import { CallEventModel } from "../call-event/call-event.model";
import { AWSGatewayAspect } from "../shared/offline";

declare const window: any;

declare const document: any;

declare const navigator: any;

const environment = new IcEnvironment().getEnvironment();

function translateLoaderFactory(httpBackend: HttpBackend) {
	return createCacheBustMultiTranslateHttpLoader(httpBackend, [
		"assets/design-system/i18n/",
		"assets/i18n/",
		"assets/@intocare/domain-l10n/domain-model/",
		"assets/@intocare/domain-l10n/domain-codes/",
	]);
}

function awsHttpConfigFactory() {
	return {
		region: "eu-west-1",
		apiKey: environment.AWS.API.KEY,
	};
}

@NgModule({
	declarations: [AppComponent, ...CUSTOM_COMPONENT_DIRECTIVES],
	exports: [AppComponent],
	imports: [
		BrowserModule,
		BrowserAnimationsModule,
		SimpleToastModule.forRoot(),
		LoadingIndicatorModule.forRoot(),
		S3ImageModule.forRoot(),
		MedicalIconModule.forRoot(),
		CordovaModule.forRoot(),
		SharedModule.forRoot(),
		ScrollModule,
		DynamicFormsModule.forRoot({
			components: {
				provide: COMPONENT_MAP,
				useFactory: customComponentFactory,
			},
		}),
		AuthSharedModule,
		HomeSharedModule,
		TasksSharedModule,
		MedDataOverviewModule,
		WoundCareOverviewModule,
		MatDialogModule,
		SyncActionsModule,
		routing,
		AppAlertBannerComponent,
	],
	providers: [
		{
			provide: ErrorHandler,
			useFactory: errorHandlerFactory({
				url: environment?.SENTRY?.URL,
				sentryReplaySessionSampleRate: environment?.SENTRY?.REPLAY_SESSION_SAMPLE_RATE,
				sentryReplayOnErrorSampleRate: environment?.SENTRY?.REPLAY_ON_ERROR_SAMPLE_RATE,
				environment: environment.ENVIRONMENT,
				version: environment.APP_VERSION,
			}),
			deps: [AWSAuthService, IcOrganisation, Router],
		},
		DarListener,
		MedicationIncidentListener,
		FoundMedicationIncidentListener,
		{ provide: PUB_SUB_MODELS, useExisting: DarListener, multi: true },
		{ provide: PUB_SUB_MODELS, useExisting: MedicationIncidentListener, multi: true },
		{ provide: PUB_SUB_MODELS, useExisting: FoundMedicationIncidentListener, multi: true },
		CallEventModel,
		{ provide: TIMER_MODELS, useExisting: CallEventModel, multi: true },
		TasksModel,
		{ provide: PUB_SUB_MODELS, useExisting: TasksModel, multi: true },
		MedicalDataModel,
		WoundCareModel,
		{ provide: PUB_SUB_MODELS, useExisting: WoundCareModel, multi: true },
		{
			provide: HTTP_INTERCEPTORS,
			useClass: TokenInterceptor,
			multi: true,
		},
		{
			provide: COGNITO_SERVICE_CONFIGURATION,
			useFactory: awsHttpConfigFactory,
		},
		LanguageService,
		provideTanStackQuery(
			new QueryClient({
				defaultOptions: {
					queries: { retry: false, throwOnError: true },
					mutations: { retry: false, throwOnError: true },
				},
			}),
		),
		provideHttpClient(withInterceptorsFromDi()),
		provideTranslateService({
			loader: {
				provide: TranslateLoader,
				useFactory: translateLoaderFactory,
				deps: [HttpBackend],
			},
		}),
		{ provide: PERSISTENT_APP_EVENT_STORE_TOKEN, useClass: AppEventLocalForageStorage },
		{ provide: EVENT_SERVICE, useClass: DefaultEventService },
	],
})
export class AppModule {
	private store = inject(Store);
	private localStorage = inject(LocalStorageService);

	constructor() {
		AWSGatewayAspect.store = this.store;
	}
}
